// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Autocomplete,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import BlockIcon from '@mui/icons-material/Block';

export const ShareModal = ({
  isOpen,
  onClose,
  threadId,
  threadTitle,
  sharedWith = [], // Array of users the thread is currently shared with
  isOwner, // Whether current user is the owner
  companyUsers = [], // All users in the company (for autocomplete)
  currentUserId, // Current user's ID (to exclude from list)
  onShare, // Callback: (userId) => void
  onRemoveAccess, // Callback: (userId) => void
  isLoading = false,
  usersWithoutGptAccess = [], // User IDs who don't have access to the thread's GPT
  gptName, // Name of the GPT (for tooltip message)
}) => {
  const [selectedUser, setSelectedUser] = useState(null);

  // Display order is independent of the server's: always alphabetical
  // by display name (fallback email), case-insensitive.
  const byName = (a, b) =>
    (a.display_name || a.email || '').localeCompare(
      b.display_name || b.email || '', undefined, { sensitivity: 'base' }
    );

  // Filter out users who already have access and the current user
  const availableUsers = companyUsers.filter(
    (user) =>
      user.id !== currentUserId &&
      !sharedWith.some((shared) => shared.id === user.id)
  ).sort(byName);
  const sortedSharedWith = [...sharedWith].sort(byName);

  // Check if a user has GPT access
  const hasGptAccess = (userId) => !usersWithoutGptAccess.includes(userId);

  const handleShare = async () => {
    if (selectedUser && onShare) {
      await onShare(selectedUser.id);
      setSelectedUser(null);
    }
  };

  const handleRemove = async (userId) => {
    if (onRemoveAccess) {
      await onRemoveAccess(userId);
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShareIcon sx={{ color: '#f97316' }} />
          <Typography variant="h6" fontWeight={600}>
            Share Conversation
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Thread info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Sharing:
          </Typography>
          <Typography variant="body1" fontWeight={500} noWrap>
            {threadTitle || 'Untitled conversation'}
          </Typography>
        </Box>

        {/* Add user section - only for owner */}
        {isOwner && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Autocomplete
                value={selectedUser}
                onChange={(e, newValue) => {
                  // Only allow selecting users with GPT access
                  if (newValue && hasGptAccess(newValue.id)) {
                    setSelectedUser(newValue);
                  }
                }}
                options={availableUsers}
                getOptionLabel={(option) => option.display_name || option.email}
                getOptionDisabled={(option) => !hasGptAccess(option.id)}
                renderOption={(props, option) => {
                  const noAccess = !hasGptAccess(option.id);
                  const content = (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        ...props.sx,
                        opacity: noAccess ? 0.5 : 1,
                        cursor: noAccess ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          mr: 1,
                          bgcolor: noAccess ? 'grey.400' : 'primary.main',
                          fontSize: '0.875rem',
                        }}
                      >
                        {noAccess ? <BlockIcon sx={{ fontSize: 16 }} /> : getInitials(option.display_name || option.email)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color={noAccess ? 'text.disabled' : 'text.primary'}>
                          {option.display_name || option.email}
                        </Typography>
                        {option.display_name && (
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        )}
                        {noAccess && (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                            No access to {gptName || 'this GPT'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );

                  return noAccess ? (
                    <Tooltip title={`This user doesn't have access to "${gptName || 'this GPT'}". Share the GPT with them first.`} placement="right">
                      <span>{content}</span>
                    </Tooltip>
                  ) : content;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search team members..."
                    size="small"
                    fullWidth
                  />
                )}
                sx={{ flex: 1 }}
                noOptionsText="No team members available"
              />
              <Button
                variant="contained"
                onClick={handleShare}
                disabled={!selectedUser || isLoading}
                startIcon={<PersonAddIcon />}
                sx={{
                  bgcolor: '#f97316',
                  '&:hover': { bgcolor: '#ea580c' },
                  whiteSpace: 'nowrap',
                }}
              >
                Add
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {/* Current participants */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {sortedSharedWith.length > 0
            ? `Shared with ${sortedSharedWith.length} ${sortedSharedWith.length === 1 ? 'person' : 'people'}`
            : 'Not shared with anyone yet'}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#f97316' }} />
          </Box>
        ) : (
          <List disablePadding>
            {sortedSharedWith.map((user) => (
              <ListItem
                key={user.id}
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem',
                    }}
                  >
                    {getInitials(user.display_name || user.email)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.display_name || user.email}
                  secondary={user.display_name ? user.email : null}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                  }}
                />
                {isOwner && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemove(user.id)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}

            {sortedSharedWith.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 4,
                  color: 'text.disabled',
                }}
              >
                <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {isOwner
                    ? 'Add team members to collaborate on this conversation'
                    : 'This conversation is private'}
                </Typography>
              </Box>
            )}
          </List>
        )}

        {/* Info text for non-owners */}
        {!isOwner && sortedSharedWith.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              You have access to this shared conversation. Only the owner can manage sharing settings.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
